import { IHateoasLink } from '@store'

// The server owns the filename rule (BRAND-9636), so prefer what it sends.
// Parsed rather than split on 'filename=' because a Rails response carries both
// `filename` and `filename*`, and a naive split drags the second into the first.
export const filenameFromContentDisposition = (header?: string | null): string | undefined => {
  if (!header) return undefined

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim())
    } catch {
      // Malformed percent-encoding in the RFC 5987 parameter. This does NOT
      // "fall through" to a value read from the plain parameter below: the
      // plain regex requires a literal `filename=`, and a header carrying
      // only `filename*=` has no such thing, so it won't match either — this
      // returns undefined.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1].trim() : undefined
}

// A server-supplied name is untrusted input reaching a DOM attribute — not
// exploitable via `download` (browsers sanitise it), but stripping path
// separators costs nothing and rules out a mangled `../../evil.csv`-shaped
// save name from a malformed or hostile Content-Disposition.
const sanitiseFilename = (filename: string): string => filename.replace(/[/\\]/g, '_')

// `preferServerFilename` opts a caller into the server's Content-Disposition
// filename (BRAND-9636's shortlist CSV, which needs the server-owned name).
// Existing callers default to false and keep their own filename, byte-identical
// to their pre-BRAND-9636 behaviour.
export const downloadCSV = (
  link: IHateoasLink,
  filename: string,
  headers: Record<string, string>,
  { preferServerFilename = false }: { preferServerFilename?: boolean } = {},
) => {
  return fetch(link.href, {
    method: link.method,
    headers,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to download CSV: ${response.status}`)
      }

      return response
        .text()
        .then((text) => ({ text, disposition: response.headers.get('Content-Disposition') }))
    })
    .then(({ text, disposition }) => {
      const data = new Blob([text], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(data)
      const anchor = document.createElement('a')
      anchor.setAttribute('href', url)
      const serverFilename = preferServerFilename
        ? filenameFromContentDisposition(disposition)
        : undefined
      anchor.setAttribute('download', sanitiseFilename(serverFilename ?? filename))
      anchor.click()
      // The anchor is never appended to the DOM, so its blob fetch is a
      // queued task rather than something the synchronous click forces to
      // start immediately — revoking the object URL in the same tick is a
      // documented race in Firefox/Safari. Defer by a tick so that fetch
      // gets a chance to start before the URL dies.
      setTimeout(() => window.URL.revokeObjectURL(url), 0)
    })
}

export const downloadZIP = (link: IHateoasLink, headers) => {
  return fetch(link.href, {
    method: link.method,
    headers,
  }).then((response) => {
    const contentDispositionHeader = response.headers.get('Content-Disposition')
    const filename = contentDispositionHeader
      ? contentDispositionHeader.split('filename=')[1].replace(/"|;/g, '')
      : ''

    return response.arrayBuffer().then((data) => {
      const blob = new Blob([data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.setAttribute('href', url)
      anchor.setAttribute('download', filename || '')
      anchor.click()
      window.URL.revokeObjectURL(url)
    })
  })
}
